import { useState } from "react";
import { Search, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";

interface TagProfilesTabProps { tagId: string; }

export const TagProfilesTab = ({ tagId }: TagProfilesTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: policies = [], isLoading } = useQuery({
    queryKey: ["tag-policies", tagId],
    queryFn: () => tagsApi.getPoliciesByTag(tagId),
  });

  const filteredPolicies = policies.filter((p: any) =>
    (p.name || p.policyName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse"><CardHeader><div className="h-4 bg-muted rounded w-3/4"></div></CardHeader></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search profiles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </div>
      {filteredPolicies.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-center mb-2">No Profiles Found</CardTitle>
          <CardDescription className="text-center">{searchQuery ? "No profiles match your search." : "No profiles assigned to this tag yet."}</CardDescription>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPolicies.map((policy: any) => (
            <Card key={policy.id || policy.policyId} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{policy.name || policy.policyName}</CardTitle>
                {policy.description && <CardDescription className="text-sm line-clamp-2">{policy.description}</CardDescription>}
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
